/**
 * 기능별 데이터 로딩 속도 축적기 (순수 로직).
 *
 * TanStack Query 의 fetch 1건 = 사용자가 기다린 데이터 1덩어리다. 이것을 쿼리 키 첫
 * 원소(`students`, `payment-requests` …)를 "기능" 이름으로 삼아 모은다.
 *
 * 텔레메트리 수집 엔드포인트는 요청 1건에 이벤트 1건만 받는다. fetch 마다 보내면
 * 페이지 하나에 수십 건이 나가므로, 경로(pathname) 단위로 모아 두었다가 한 번에
 * `custom` 이벤트 1건(kind: "query_timing")으로 내보낸다.
 *
 * 속성 키 이름 주의: 텔레메트리 sanitizer 는 키에 email|phone|address|content|message|
 * memo|text|body|query|token|password|secret 가 들어가면 값을 `[redacted]` 로 바꾼다.
 * 그래서 "queryKey" 가 아니라 "op" 를 쓴다. 기능 이름은 키가 아니라 값에 둔다
 * (`addresses` 같은 쿼리 키가 속성 키가 되면 통째로 가려진다).
 */

/** 한 이벤트의 ops 배열 상한. sanitizer 는 배열을 50개에서 자른다. */
const MAX_OPS_PER_BATCH = 40
/** 기능 1개당 원본 샘플 보관 수. 서버에서 정확한 백분위를 낼 수 있게 남긴다. */
const MAX_SAMPLES_PER_OP = 20
/** 노트북 덮개를 닫는 등으로 멈춰 있던 요청은 속도 지표가 아니다. */
export const MAX_QUERY_DURATION_MS = 5 * 60_000

export interface QueryTimingSample {
  pathname: string
  op: string
  durationMs: number
  /** 캐시에 데이터가 없어 사용자가 로딩 화면을 본 fetch */
  cold: boolean
  ok: boolean
}

export interface QueryTimingOpSummary {
  op: string
  count: number
  coldCount: number
  errorCount: number
  totalMs: number
  maxMs: number
  samplesMs: number[]
}

export interface QueryTimingBatch {
  pathname: string
  /** 첫 샘플부터 내보낼 때까지의 시간 */
  windowMs: number
  ops: QueryTimingOpSummary[]
}

interface Bucket {
  openedAt: number
  ops: Map<string, QueryTimingOpSummary>
}

export class QueryTimingAccumulator {
  private readonly buckets = new Map<string, Bucket>()

  constructor(private readonly now: () => number = () => Date.now()) {}

  record(sample: QueryTimingSample): void {
    if (!Number.isFinite(sample.durationMs) || sample.durationMs < 0) return
    if (sample.durationMs > MAX_QUERY_DURATION_MS) return

    let bucket = this.buckets.get(sample.pathname)
    if (!bucket) {
      bucket = { openedAt: this.now(), ops: new Map() }
      this.buckets.set(sample.pathname, bucket)
    }

    let summary = bucket.ops.get(sample.op)
    if (!summary) {
      summary = { op: sample.op, count: 0, coldCount: 0, errorCount: 0, totalMs: 0, maxMs: 0, samplesMs: [] }
      bucket.ops.set(sample.op, summary)
    }

    const durationMs = Math.round(sample.durationMs)
    summary.count += 1
    if (sample.cold) summary.coldCount += 1
    if (!sample.ok) summary.errorCount += 1
    summary.totalMs += durationMs
    summary.maxMs = Math.max(summary.maxMs, durationMs)
    if (summary.samplesMs.length < MAX_SAMPLES_PER_OP) summary.samplesMs.push(durationMs)
  }

  hasPending(): boolean {
    return this.buckets.size > 0
  }

  /** 모인 것을 전부 꺼내고 비운다. 기능이 많으면 여러 배치로 나눈다. */
  drain(): QueryTimingBatch[] {
    const now = this.now()
    const batches: QueryTimingBatch[] = []

    for (const [pathname, bucket] of this.buckets) {
      const ops = [...bucket.ops.values()].sort((a, b) => b.totalMs - a.totalMs)
      for (let i = 0; i < ops.length; i += MAX_OPS_PER_BATCH) {
        batches.push({
          pathname,
          windowMs: Math.max(0, Math.round(now - bucket.openedAt)),
          ops: ops.slice(i, i + MAX_OPS_PER_BATCH),
        })
      }
    }

    this.buckets.clear()
    return batches
  }
}

/** 쿼리 키 → 기능 이름. 첫 원소가 문자열이 아니면 집계 대상이 아니다. */
export function toQueryOpName(queryKey: readonly unknown[]): string | null {
  const head = queryKey[0]
  if (typeof head !== 'string' || !head.trim()) return null
  return head.trim().slice(0, 80)
}

/**
 * `/center/students/123` → `/center/students/[id]`.
 * OS 에러 그룹의 라우트 정규화(normalizeRoutePattern)와 같은 규칙이라
 * 속도 지표와 에러를 같은 화면 단위로 맞춰 볼 수 있다.
 */
export function toRoutePattern(pathname: string): string {
  const path = (pathname.split(/[?#]/)[0] ?? '').replace(/\/+/g, '/').replace(/(.)\/+$/, '$1')
  if (!path || path === '/') return '/'

  const segments = path.replace(/^\/+/, '').split('/')
  return `/${segments.map((segment) => (isIdLikeSegment(segment) ? '[id]' : segment)).join('/')}`
}

function isIdLikeSegment(segment: string): boolean {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment)) return true
  if (/^[0-9]+$/.test(segment)) return true
  if (/^[0-9a-f]{16,}$/i.test(segment)) return true
  // 길고 숫자가 섞인 slug (nanoid, 결제 토큰 등)
  return segment.length >= 16 && /[0-9]/.test(segment) && /^[a-z0-9_-]+$/i.test(segment)
}
